import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { LINE_CLEARANCE_PATHS } from "../../../../Routes/paths";
import { useGlobalStudy } from "../../../../context/StudyContext";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useLineClearanceService } from "../api/useLineClearance";
import { formatDate } from "../../../../common/service/CommonService";
import SectionCard from "Components/Common/SectionCard";
import FullPageLoader from "Components/Common/FullPageLoader";
import DetailField from "../../../../Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";
import LineClearanceTable from "../components/LineClearanceTable";
import { LineClearanceRow, LineClearanceViewTab } from "../helpers/lineClearance.types";

const VIEW_TABS: { key: LineClearanceViewTab; label: string; icon: string }[] = [
  { key: "pharmacist_details", label: "Pharmacist Clearance Details", icon: "ri-file-text-line" },
  { key: "qa_verification", label: "QA Verification Details", icon: "ri-checkbox-circle-line" },
];

const ViewLineClearance: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<LineClearanceViewTab>("pharmacist_details");
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy: globalStudy } = useGlobalStudy();

  document.title = "View Line Clearance | ZENTIXS ORBIT";

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Line Clearance", link: LINE_CLEARANCE_PATHS.LIST },
      { text: "View Line Clearance" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const { SelectQuery } = useLineClearanceService({
    lineclearanceid: id ? Number(id) : null,
  });

  const data = useMemo(() => {
    const rawData = SelectQuery.data?.data;
    if (Array.isArray(rawData)) return rawData[0];
    return rawData || null;
  }, [SelectQuery.data]);

  const checklistRows: LineClearanceRow[] = useMemo(() => {
    if (data?.checklist && Array.isArray(data.checklist)) {
      return data.checklist.map((q: any, idx: number) => ({
        id: q.lineclearancechecklistid || idx + 1,
        particulars: q.checklistquestion || "",
        pharmacistInfo: q.performedanswer || "yes",
        qaInfo: q.reviewedanswer || "yes",
        qaRemark: q.remark || "",
      }));
    }
    return [];
  }, [data]);

  if (SelectQuery.isLoading || SelectQuery.isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader message="Loading Line Clearance details..." />
      </Container>
    );
  }

  /* ── Not Found ── */
  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">
              The Line Clearance record you are looking for does not exist or has been removed.
            </p>
            <Button
              color="primary"
              outline
              onClick={() => navigate(LINE_CLEARANCE_PATHS.LIST)}
            >
              Back to List
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const pharmacistDecisionLabel =
    data.performerdecision !== undefined && data.performerdecision !== null
      ? data.performerdecision
        ? "The line is clear and can proceed for next operation."
        : "The line is not clear and cannot proceed for next operation."
      : "—";

  const qaDecisionLabel =
    data.reviewerdecision !== undefined && data.reviewerdecision !== null
      ? data.reviewerdecision
        ? "The line is clear and can proceed for next operation."
        : "The line is not clear and cannot proceed for next operation."
      : "—";

  return (
    <div className="page-content">
      <Container fluid>
        {/* ── Header Summary ── */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Line Clearance Details</h3>
            <p className="text-muted mb-0">
              Study: {data.studycode}&nbsp;|&nbsp;
              IP Category: {data.ipcategory || "—"}&nbsp;|&nbsp;
              Product: <TruncatedText text={data.productname || "—"} maxWidth="200px" />&nbsp;|&nbsp;
              Period: {data.period_number || data.periodnumber || "—"}&nbsp;|&nbsp;
              Clearance Date: {data.clearancedate ? formatDate(data.clearancedate) : "—"}
            </p>
          </div>
        </div>

        {/* ── Tab Switcher ── */}
        <div className="mb-4">
          <div className="p-1 bg-light rounded-pill d-inline-flex border">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 d-flex align-items-center gap-1 ${
                  activeTab === tab.key ? "text-primary shadow-sm" : "text-muted"
                }`}
                style={{
                  fontSize: "12px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  backgroundColor: activeTab === tab.key ? "#ffffff" : "transparent",
                  transition: "background-color 0.15s ease",
                }}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={tab.icon} style={{ fontSize: "13px" }} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab 1: Pharmacist Clearance Details */}
        {activeTab === "pharmacist_details" ? (
          <div className="d-flex flex-column gap-0.5">
            {/* Section 1: Line Clearance Details */}
            <SectionCard
              index={1}
              icon="ri-file-text-line"
              title="Line Clearance Details"
              subtitle="Study protocol, reason, product details, and clearance date."
            >
              <Row>
                <DetailField label="Study Code" value={data.studycode} columns={3} />
                <DetailField label="IP Category" value={data.ipcategory} columns={3} />
                <DetailField label="Period" value={data.period_number || data.periodnumber} columns={3} />
                <DetailField label="Product Name" value={data.productname} columns={3} truncate={true} />
                <DetailField
                  label="Date of Line Clearance"
                  value={data.clearancedate ? formatDate(data.clearancedate) : null}
                  columns={3}
                />
                <DetailField label="Time of Line Clearance" value={data.clearancetime} columns={3} />
                <DetailField label="Reason for Line Clearance" value={data.reasonforclearance} columns={6} truncate={true} />
              </Row>
            </SectionCard>

            {/* Section 2: Line Clearance Checklist Table */}
            <SectionCard
              index={2}
              icon="ri-checkbox-multiple-line"
              title="Line Clearance Checklist (Pharmacist)"
              subtitle="Pharmacist (A) verification checklist across all parameters."
            >
              <LineClearanceTable
                checklist={checklistRows}
                hideQaColumns={true}
                isDisabled={true}
              />
            </SectionCard>

            {/* Section 3: Declaration by Pharmacist */}
            <SectionCard
              index={3}
              icon="ri-shield-check-line"
              title="Line Clearance Declaration (Pharmacist)"
              subtitle="Clearance status decision recorded by Pharmacist / Designee."
            >
              <Row>
                <DetailField
                  label="Clearance Status Decision (Pharmacist)"
                  value={
                    data.performerdecision !== undefined && data.performerdecision !== null ? (
                      <span
                        className={`badge ${
                          data.performerdecision
                            ? "bg-success-subtle text-success border border-success-subtle"
                            : "bg-danger-subtle text-danger border border-danger-subtle"
                        } px-3 py-2 rounded-pill fs-12`}
                      >
                        {pharmacistDecisionLabel}
                      </span>
                    ) : (
                      "—"
                    )
                  }
                  columns={6}
                />
                {data.performedbyname && (
                  <DetailField label="Performed By" value={data.performedbyname} columns={6} />
                )}
              </Row>
            </SectionCard>
          </div>
        ) : (
          /* Tab 2: QA Verification Details */
          <div className="d-flex flex-column gap-0.5">
            {/* Section 1: QA Verification Checklist */}
            <SectionCard
              index={1}
              icon="ri-checkbox-multiple-line"
              title="Line Clearance QA Verification Checklist"
              subtitle="Inspection records, QA verification answers, and remarks."
            >
              {checklistRows.length > 0 ? (
                <LineClearanceTable
                  checklist={checklistRows}
                  hideQaColumns={false}
                  isDisabled={true}
                />
              ) : (
                <div className="text-muted fst-italic py-3 text-center fs-13">
                  No QA verification details recorded.
                </div>
              )}
            </SectionCard>

            {/* Section 2: QA Declaration & Decision */}
            <SectionCard
              index={2}
              icon="ri-shield-check-line"
              title="Line Clearance Verification (QA Person)"
              subtitle="Final clearance status decision by QA Person."
            >
              <Row>
                <DetailField
                  label="Clearance Status Decision (QA Person)"
                  value={
                    data.reviewerdecision !== undefined && data.reviewerdecision !== null ? (
                      <span
                        className={`badge ${
                          data.reviewerdecision
                            ? "bg-success-subtle text-success border border-success-subtle"
                            : "bg-danger-subtle text-danger border border-danger-subtle"
                        } px-3 py-2 rounded-pill fs-12`}
                      >
                        {qaDecisionLabel}
                      </span>
                    ) : (
                      "Pending QA Review"
                    )
                  }
                  columns={6}
                />
                {data.reviewedbyname && (
                  <DetailField label="Reviewed By" value={data.reviewedbyname} columns={6} />
                )}
              </Row>
            </SectionCard>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ViewLineClearance;
